﻿import React, { useState, useEffect } from 'react';
import AssetHeaderComponent from './AssetHeaderComponent';
import GcAssetComponent from './GcAssetComponent';
import HplcAssetComponent from './HplcAssetComponent';
import { BenchSheetTechnologyType } from '../models/enums';
import { Bin } from '../models/Locations/bin';
import { LookupItem } from '../models/lookup-item';
import { Asset } from '../models/asset';
import { AssetData } from '../models/asset-data';
import { GasChromatographyAsset } from '../models/gas-chromatography-asset';
import { HplcAsset } from '../models/hplc-asset';
import { AssetUpdateRequest } from '../models/Assets/asset-update-request';
import GlobalDialog from './Modal/GlobalDialog';
import { ModalMetadata } from '../models/modal-metadata';
import { ModalMessageType } from '../models/enums';
import { CrudFunctions } from '../utilities/crud-functions';
import { ToastProps } from '../models/toast-props';

import { Button } from 'devextreme-react/button';
import { Toast } from 'devextreme-react/toast';
import { List } from 'devextreme-react/list';
import { TabPanel, Item as TabItem } from 'devextreme-react/tab-panel';
import { ScrollView } from 'devextreme-react/scroll-view';
import { DataTemplates } from '../templates/data-templates';

export interface Props {
    isReadOnly: boolean;
    assetId: string;
    isChildAsset: boolean;
    technologies?: LookupItem[];
    assetData?: AssetData;
    onClose?: () => void;
}

export default function AssetEditor(props: Props) {
    const [assetId, setAssetId] =
        useState<string>();

    const [asset, setAsset] =
        useState<Asset>();

    const [selectedTechnologies, setSelectedTechnologies] =
        useState<number[]>([]);

    // TODO logic to sync child form data back to here & update save function
    //const [formData, setFormData] =
    //    useState({ main: {}, gc: {}, hplc: {} });

    const [zoneBins, setZoneBins] =
        useState<Bin[]>();

    const [editedAsset, setEditedAsset] =
        useState<Asset>();

    const [editedGcAsset, setEditedGcAsset] =
        useState<GasChromatographyAsset>();

    const [editedHplcAsset, setEditedHplcAsset] =
        useState<HplcAsset>();

    const [toastProps, setToastProps] =
        useState<ToastProps>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>()

    const hasGcForm = selectedTechnologies?.includes(BenchSheetTechnologyType.GasChromatography as number) ||
        selectedTechnologies.includes(BenchSheetTechnologyType.GasChromatographyResidualSolvents as number);
    const hasHplcForm = selectedTechnologies?.includes(BenchSheetTechnologyType.HighPerformanceLiquidChromatography as number);

    useEffect(() => {
        if (assetId !== props.assetId)
            setAssetId(props.assetId);
    }, [props.assetId]);

    useEffect(() => {
        getAllData();
    }, [assetId]);

    useEffect(() => {
        getBinsByZoneId();
    }, [asset]);

    return (
        <div className="asset-editor-container">
            {/*HEADER*/}
            <div className="pw-comp-row pw-margin-top-05 asset-editor-header">
                <div className="pw-margin-left-05">
                    <div className="lq-sub-title pw-margin-bottom-05">Asset#: {asset && asset.formattedAssetNumber}</div>
                    <div className="lq-header-subtitle">{asset && asset.description}</div>
                </div>

                <div className="title-bar-spacer" />
                <div className="save-toolbar pw-margin-right-05">
                    <Button id="saveButton" text="Save" icon="save" type="success"
                        className="pw-button"
                        hint="save changes"
                        height='1.8rem'
                        onClick={onSaveClick} />

                    <Button id="closeButton" text="Close" icon="close" type="normal"
                        className="pw-button"
                        hint="close dialog"
                        height='1.8rem'
                        onClick={onCloseClick} />
                </div>
            </div>

            {/* FLEX ROW */}
            <div className="asset-editor-flex-row-container">

                {/* SIDE BAR w/ TECHNOLOGIES LIST - fixed width */}
                {!props.isChildAsset &&

                    <div className="asset-editor-left">
                        <div className="pw-form-unit-label">Technologies</div>

                        <List id="assetTechnologiesList"
                            className="pw-bg-white"
                            disabled={props.isReadOnly}
                            items={props.assetData.technologies ? props.assetData.technologies : []}
                            keyExpr="id"
                            showScrollbar="always"
                            itemRender={DataTemplates.EhsTemplate}
                            showSelectionControls={true}
                            selectionMode="multiple"
                            onSelectionChanged={(e) => {
                                setSelectedTechnologies(e.component.option('selectedItemKeys'));
                            }}
                            selectedItemKeys={selectedTechnologies} />
                    </div>
                }

                {/* FORMS TABPANEL - grows to fill */}
                <div className="asset-editor-right">
                    <TabPanel
                        height="100%"
                        deferRendering={false}
                        animationEnabled={false}
                        elementAttr={{ class: 'asset-editor-tab-panel' }}
                    >
                        <TabItem title="General" icon="description">
                            <ScrollView>
                                <AssetHeaderComponent isReadOnly={props.isReadOnly}
                                    asset={asset} assetData={props.assetData}
                                    bins={zoneBins} isChildAsset={props.isChildAsset}
                                    onValueChanged={onHeaderValueChanged} />
                            </ScrollView>
                        </TabItem>

                        {hasGcForm && (
                            <TabItem title="Gas Chromatography" icon="datausage">
                                <ScrollView>
                                    <GcAssetComponent isReadOnly={props.isReadOnly}
                                        assetId={assetId} assetData={props.assetData}
                                        onGcInstrumentChanged={onGcInstrumentChanged} />
                                </ScrollView>
                            </TabItem>
                        )}

                        {hasHplcForm && (
                            <TabItem title="HPLC" icon="stickcolumn">
                                <ScrollView>
                                    <HplcAssetComponent isReadOnly={props.isReadOnly}
                                        assetId={assetId} assetData={props.assetData}
                                        onHplcInstrumentChanged={onHplcInstrumentChanged} />
                                </ScrollView>
                            </TabItem>
                        )}

                    </TabPanel>

                </div>

            </div>

            {toastProps && toastProps.visible &&
                <Toast
                    visible={toastProps.visible}
                    message={toastProps.message}
                    type={toastProps.toastType}
                    onHiding={onToastHiding}
                    displayTime={600} />
            }

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} />
            }

        </div>
    );

    function onHeaderValueChanged(editedAsset: Asset) {
        console.log("onHeaderValueChanged");
        console.log(editedAsset);

        setEditedAsset(editedAsset);
    }

    function onGcInstrumentChanged(instrument: GasChromatographyAsset) {
        console.log("AssetEditor.onGcInstrumentChanged...");
        console.log("instrument: ", instrument);
        setEditedGcAsset(instrument);
    }

    function onHplcInstrumentChanged(instrument: HplcAsset) {
        console.log("onHplcInstrumentChanged");
        console.log(instrument);

        setEditedHplcAsset(instrument);
    }

    async function getAllData() {
        if (assetId)
            await getAsset();
    }

    async function getAsset() {
        await CrudFunctions.getData(`assets/assetbyassetid/${assetId}`)
            .then((data: Asset) => {
                console.log("ASSET--");
                console.log(data);

                setAsset(data);
                setSelectedTechnologies(data.technologies?.map(item => item.id) ?? []);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Selected Asset");
            });
    }

    async function getBinsByZoneId() {
        console.log("getBinsByZoneId");
        console.log(asset);


        if (asset && asset.zoneId) {
            await CrudFunctions.getData(`common/locationbins/${asset.zoneId}`)
                .then(data => {

                    console.log("BINS:");
                    console.log(data);

                    setZoneBins(data);
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Retrieving Selected Asset");
                });
        }
    }

    async function onSaveClick() {
        console.log("editedAsset:")
        console.log(editedAsset)
        console.log("editedGcAsset:")
        console.log(editedGcAsset)
        console.log("editedHplcAsset:")
        console.log(editedHplcAsset)

        if (!editedAsset) return;

        const rawAssetNumber = editedAsset.formattedAssetNumber
            ? parseInt(editedAsset.formattedAssetNumber, 10)
            : editedAsset.assetNumber;

        // TODO Need dirty tracker: if gc/hplc unedited, set their values to null and db will skip processing them 
        // TODO also need to handle when only gc or hplc edited & main asset data not edited (still allow db to update it?)
        const payload: AssetUpdateRequest = {
            ...editedAsset,
            assetNumber: rawAssetNumber,
            technologies: props.isChildAsset
                ? undefined
                : selectedTechnologies.map(id => ({
                    id: id,
                })),
            gasChromatographyAsset: (!props.isChildAsset && hasGcForm && editedGcAsset)
                ? editedGcAsset : null,
            hplcAsset: (!props.isChildAsset && hasHplcForm && editedHplcAsset)
                ? {
                    ...editedHplcAsset,
                    pumpTypeId: editedHplcAsset.pumpType?.id,
                } : null
        };

        const url = props.isChildAsset
            ? `assets/child-assets/${editedAsset.assetId}`
            : "assets/asset"

        await updateAsset(url, payload)
            .then(data => {
                showToast("Asset saved successfully", "success");
                setEditedAsset(null);
                setEditedGcAsset(null);
                setEditedHplcAsset(null);
                getAsset();
            })
            .catch((res) => {
                console.log("error resp:", res);
                showMessage(res, ModalMessageType.Error, "Error Updating Asset Data");
            });
    }

    async function updateAsset(url: string, assetPayload: AssetUpdateRequest) {
        return await CrudFunctions.putData(url, assetPayload)
    }

    function onCloseClick() {
        if (props.onClose)
            props.onClose();
    }

    function showToast(message: string, toastType: string) {
        let toastParams: ToastProps = {
            message: message,
            toastType: toastType,
            visible: true
        };

        setToastProps(toastParams);
    }

    function onToastHiding() {
        let toastParams: ToastProps = {
            message: undefined,
            toastType: undefined,
            visible: false
        };

        setToastProps(toastParams);
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }
}


